import {useNotifications} from 'app/toasts/hooks';
import {gt} from 'app/utils/i18n';

import {extendedAPI} from './api';

import type {
  ProcedureTaskCoreWithVersion,
  ProcessTaskCoreWithVersion,
} from 'sweet-taskinfo';
import type {
  AnyTaskStepType,
  SlimTaskInstanceSerializedType,
  TaskConcreteInstanceSerializedType,
  TaskInstanceSerializedType,
  UpdateTaskPayloadType,
} from 'sweet-types';

/* Provide function to update task instances */
export const useTaskUpdate = (
  customUpdateTaskInstance?: (args: {
    url: string;
    update: UpdateTaskPayloadType;
  }) => Promise<TaskConcreteInstanceSerializedType>
) => {
  const {showToastNotification} = useNotifications();

  const [defaultUpdateTaskInstance] =
    extendedAPI.endpoints.updateTaskInstance.useMutation();

  const updateTaskInstance =
    customUpdateTaskInstance ??
    ((...args: Parameters<typeof defaultUpdateTaskInstance>) =>
      defaultUpdateTaskInstance(...args).unwrap());

  return {
    updateTaskInstance,
    toggleTaskCompleted: (
      task: TaskInstanceSerializedType | SlimTaskInstanceSerializedType
    ): Promise<TaskConcreteInstanceSerializedType> => {
      const isCompleting = task.completed == null;

      return updateTaskInstance({
        url: task.url,
        update: {
          completed: isCompleting ? new Date().toISOString() : null,
        },
      }).then((response) => {
        showToastNotification(
          isCompleting ? gt('Task completed') : gt('Task set as incomplete')
        );
        return response;
      });
    },
    updateTaskStep: (
      taskCore: ProcedureTaskCoreWithVersion | ProcessTaskCoreWithVersion,
      step: AnyTaskStepType,
      checked: boolean
    ): Promise<TaskConcreteInstanceSerializedType> => {
      // extract progress field with step changes
      const progressPayload =
        taskCore.ct === 'process' && step.type === 'processprocedure'
          ? taskCore.payload.toSetProcedureCompleted(step.id, checked)
          : taskCore.ct === 'process'
          ? taskCore.payload.toSetDecisionStepCompleted(step.id, null)
          : taskCore.payload.toSetStepCompleted(step.id, checked);

      return updateTaskInstance({
        url: taskCore.task.url,
        update: progressPayload,
      });
    },
  };
};
